import { z } from 'zod'
import { useZorm } from 'react-zorm'
import { FormField, fieldDescription } from '@codecorp/zorm-conventions'
import { useMemo, useState } from 'react'
import { ShowBarcode } from '../components/show-barcode'
import { useDropzone } from 'react-dropzone'
import { Button } from '@codecorp/components'
import {
    maxSplitJavaScript,
    splitJavascript,
} from '@codecorp/barcode-data-generator'
import { useFormReset } from '../hooks/use-form'
import qrCodeGen from 'qrcode'

const FormSchema = (
    minNumBarRange: number,
    maxSegmentRange: number,
    file: File | undefined,
) =>
    z
        .object({
            label: z.string().describe(
                fieldDescription({
                    placeHolder: 'This name will be placed under the QR Code.',
                }),
            ),
            jsFile: z.instanceof(File),
            numOfBarcodes: z
                .string()
                .optional()
                .transform((x) =>
                    x != null && x !== '' ? Number.parseInt(x) : undefined,
                )
                .refine((val) => val == null || val >= minNumBarRange, {
                    message: `The Number Of Barcodes should be from ${Math.ceil(
                        minNumBarRange,
                    )} or higher.`,
                })
                .describe(
                    fieldDescription({
                        label: 'Number Of Barcodes',
                        inputType: 'number',
                        placeHolder:
                            'Split the file at given number of barcodes',
                    }),
                ),
            fileSize: z
                .string()
                .regex(/^\d{1,6}$|^$/)
                .optional()
                .transform((x) =>
                    x != null && x !== '' ? Number.parseInt(x) : undefined,
                )
                .describe(
                    fieldDescription({
                        inputType: 'number',
                        label: 'Max Segment Size',
                        placeHolder:
                            'Split the file with specified size in bytes',
                    }),
                ),
        })
        .refine(
            (data) => {
                const { fileSize } = data
                if (fileSize && file) {
                    return Math.ceil(fileSize) <= Math.ceil(maxSegmentRange)
                }
                // If fileSize or file is not provided, we consider it valid
                return true
            },
            {
                message: `Max Segment Size should be from ${Math.ceil(
                    maxSegmentRange,
                )} or lower.`,
                path: ['fileSize'],
            },
        )
export const SplitJsBarcodeCreator = () => {
    const { submittedBarcode, setSubmittedBarcode, handleFormReset } =
        useFormReset(undefined)
    const [fileName, setFileName] = useState<string | null>(null)
    const [fileContent, setFileContent] = useState('')
    const [result, setResult] = useState<ReturnType<typeof splitJavascript>>()
    const [error, setError] = useState('')
    const [barcodePartsArray, setBarcodePartsArray] = useState<string[]>([])

    const [file, setFile] = useState<File>()
    const [maxSegmentRange, setMaxSegmentRange] = useState(0)
    const [minNumBarRange, setMinNumBarRange] = useState(0)

    // Memoize the form schema to prevent unnecessary recalculations
    // Use useMemo: This ensures that the form schema is only recalculated when its dependencies (minNumBarRange, minSegmentRange, maxSegmentRange, file) change, preventing unnecessary re-renders and infinite loops.
    const schema = useMemo(
        () => FormSchema(minNumBarRange, maxSegmentRange, file),
        [minNumBarRange, maxSegmentRange, file],
    )

    const zo = useZorm('jsBarcode', schema, {
        onValidSubmit: async ({ preventDefault, data }) => {
            preventDefault()
            if (file) {
                data.jsFile = file
                const sizeOfChunk = data.fileSize
                const numOfBarcodes = data.numOfBarcodes
                let barCodes
                if (file && fileName && sizeOfChunk) {
                    barCodes = splitJavascript({
                        file: { name: fileName, contents: fileContent },
                        split: { maxSize: sizeOfChunk },
                    })
                } else if (file && fileName && numOfBarcodes) {
                    barCodes = splitJavascript({
                        file: { name: fileName, contents: fileContent },
                        split: { numberBarcodes: numOfBarcodes },
                    })
                }
                if (!fileName?.includes('.js'))
                    setError('Only JavaScript files are allowed.')
                else setError('')

                if (barCodes) {
                    const updatedBarcodePartsArray = [
                        ...barCodes.parts,
                        barCodes.combine,
                    ]
                    setBarcodePartsArray(updatedBarcodePartsArray)
                    setResult(barCodes)
                }
            } else {
                setError('No file selected.')
            }
            setSubmittedBarcode(data)
        },
    })

    const onDrop = async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0]
        //Reset the result once user upload new file
        setResult(undefined)

        if (file) {
            setFile(file)
            const content = await file.text() // Read the file content as text
            setFileContent(content) // Set the content to state
            setFileName(file.name) // Set the file name to state
            setError('')
            const MAX_SEGMENT_SIZE = maxSplitJavaScript(
                {},
                { name: file.name || '', contents: content },
            )
            const maxSegmentSize = MAX_SEGMENT_SIZE[0]

            // Set the ranges
            //The math here is to make the ranges of Max Segment Size and Number Of Barcodes match
            setMaxSegmentRange(maxSegmentSize)
            setMinNumBarRange(MAX_SEGMENT_SIZE[1]) //
        }
    }

    const clearFile = () => {
        setFileName(null) // Clear the file name state
        setFile(undefined)
    }

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: {
            'application/javascript': ['.js'], // Accept only .js files
        },
    })
    const handleClear = () => {
        setFileName(null) // Clear the file name
        setFileContent('') // Clear the file contents
        setResult(undefined)
        setFile(undefined)
        setError('')
    }

    //Download function
    const downloadQRCode = async () => {
        const { jsPDF } = await import('jspdf')
        const pdf = new jsPDF()
        const qrWidth = 103 // Adjust QR code size
        const qrHeight = 103
        const pageWidth = pdf.internal.pageSize.getWidth()
        const centerX = pageWidth / 2 - qrWidth / 2 // Center horizontally
        const marginY = 15 // Top margin
        const spacingY = 120 // Vertical spacing between QR codes
        let qrPerPage = 0

        for (let i = 0; i < barcodePartsArray.length; i++) {
            const data = barcodePartsArray[i]
            if (!data) continue

            // Create a canvas for QR code
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            if (!ctx) continue

            // Wait for QR code to be drawn before proceeding
            await qrCodeGen.toCanvas(canvas, data, {
                margin: 10,
                width: qrWidth,
            })
            // Add label below QR code
            const labelText = data.includes('combinerScript')
                ? 'Combiner Code'
                : `${
                      submittedBarcode.label
                          ? `${submittedBarcode.label} -`
                          : ''
                  } Segment ${i + 1}`
            // Convert canvas to image
            const imgData = canvas.toDataURL('image/png')

            // Calculate Y position for QR code (first or second position on the page)
            const yPos = marginY + (qrPerPage % 2) * spacingY
            // Add QR Code to PDF
            pdf.addImage(imgData, 'PNG', centerX, yPos, qrWidth, qrHeight)
            pdf.text(labelText, pageWidth / 2, yPos + qrHeight, {
                align: 'center',
            })

            qrPerPage++
            if (qrPerPage % 2 === 0 && i !== barcodePartsArray.length - 1) {
                pdf.addPage()
                qrPerPage = 0
            }
        }

        pdf.save(`${fileName || 'barcodes'}.pdf`)
    }

    //Print function
    const printQRCode = () => {
        const printWindow = window.open('', '_blank')
        if (!printWindow) return

        printWindow.document.write(`
            <html>
            <head>
                <title>CodeGen</title>
                <style>
                    @page { size: auto; margin-top: 50px;  }
                    body {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: top;
                        height: 100vh;
                        margin: 0;
                        padding: 0;
                        font-family:"Arial", sans-serif;
                        font-size: 20px;
                    }
                    .canvas-container {
                        margin-bottom: 20px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                </style>
            </head>
            <body>
        `)

        const canvas = document.createElement('canvas')
        for (let i = 0; i < barcodePartsArray.length; i++) {
            const data = barcodePartsArray[i] // Make sure it's defined

            if (data) {
                qrCodeGen.toCanvas(canvas, data) // Ensure data is not undefined
                const imgData = canvas.toDataURL('image/png')
                printWindow.document.write(`
                    <div class="canvas-container">
                        <img src="${imgData}" style="width: 350px; height: 350px; " />
                        <p>${
                            data.includes('combinerScript')
                                ? 'Combiner Code'
                                : `${
                                      submittedBarcode.label
                                          ? `${submittedBarcode.label} -`
                                          : ''
                                  } Segment ${i + 1}`
                        }</p>
                    </div>
                `)
            }
        }

        printWindow.document.write(`
            </body>
            </html>
        `)
        printWindow.document.close() // Close the document to apply changes

        printWindow.onload = () => {
            printWindow.focus() // Bring print window to front
            printWindow.print() // Trigger print dialog
            printWindow.close() // Close print window after printing
        }
    }

    return (
        <>
            <form
                ref={zo.ref}
                className="ml-10 mr-10 leading-10"
                onReset={handleFormReset}
            >
                {zo.fields.label(FormField)}
                <label>Upload JS File</label>
                <div
                    {...getRootProps()}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center ${
                        isDragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300'
                    }`}
                >
                    <input
                        {...getInputProps()}
                        name="jsFile"
                    />
                    <p>
                        {fileName
                            ? `Selected file: ${fileName}`
                            : "Drag 'n' drop a JavaScript file here, or click the Import a File button to upload file."}
                        <br />
                        <Button
                            filled
                            onPress={open}
                        >
                            Import a File
                        </Button>
                    </p>
                </div>

                {fileName && (
                    <div className="mt-2">
                        <button
                            type="button"
                            className="rounded-md bg-red-700 px-4 py-2 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-700"
                            onClick={clearFile}
                        >
                            Clear
                        </button>
                    </div>
                )}
                {file && (
                    <div className="text-red-600">
                        Allowed Max Segment Size range (Bytes) from{' '}
                        {Math.ceil(maxSegmentRange)} or lower
                        <br />
                        Allowed Number Of Barcodes range from{' '}
                        {Math.ceil(minNumBarRange)} or higher.
                    </div>
                )}

                {error && <p className="mt-2 text-sm text-red-600">{error} </p>}
                <div className="mt-5 text-center text-xl text-red-500">
                    Please choose 1 of the options below
                </div>

                {zo.fields.fileSize(FormField)}
                <h2 className="m-5 text-center text-xl">Or</h2>
                {zo.fields.numOfBarcodes(FormField)}

                {/* <CreatorButtons /> */}
                <Button
                    type="reset"
                    filled
                    secondary
                    className="mr-5 mt-5"
                    onPress={handleClear}
                >
                    Clear
                </Button>
                <Button
                    type="submit"
                    filled
                    className="hover:bg-blue-800"
                >
                    Generate
                </Button>
            </form>

            {result != null && (
                <div>
                    {result.parts.map((chunk, index) => (
                        <div
                            key={index}
                            className="mb-4"
                        >
                            <h4 className="text-md ml-10 mt-5 font-semibold">
                                Segment {index + 1}:
                            </h4>

                            <div className="mx-auto mt-6 flex w-full max-w-md justify-center">
                                <ShowBarcode
                                    label={`${
                                        submittedBarcode?.label
                                            ? submittedBarcode?.label + ' -'
                                            : ''
                                    } Segment ${index + 1}`}
                                    barcode={chunk}
                                    showPrintDownloadButtons={false}
                                />
                            </div>
                        </div>
                    ))}
                    <div className="mb-4">
                        <h4 className="text-md ml-10 mt-5 font-semibold">
                            Combiner Code
                        </h4>

                        <div className="mx-auto mt-6 flex w-full max-w-md justify-center">
                            <ShowBarcode
                                label={`${
                                    submittedBarcode?.label
                                        ? submittedBarcode?.label + ' -'
                                        : ''
                                } Combiner Code`}
                                barcode={result.combine}
                                showPrintDownloadButtons={false}
                            />
                        </div>
                    </div>
                    <div className="flex justify-center gap-8 self-stretch print:hidden">
                        <Button
                            className="flex-1"
                            onPress={printQRCode}
                            filled
                        >
                            Print
                        </Button>

                        <Button
                            className="flex-1"
                            onPress={downloadQRCode}
                            filled
                        >
                            Download
                        </Button>
                    </div>
                </div>
            )}
        </>
    )
}
